import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast, { Toaster } from 'react-hot-toast';
import { authApi } from '../api/client';
import { getApiErrorMessage } from '../api/errors';
import { usePageTitle } from '../hooks/usePageTitle';
import { Image } from '../components/common/NextAdapters';
import styles from './LoginPage.module.css';
import { FiLock, FiEye, FiEyeOff, FiCheckCircle, FiXCircle } from 'react-icons/fi';

export default function ResetPasswordPage() {
  usePageTitle('Reset Password · Kast');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const linkIsValid = Boolean(userId && token);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) { toast.error('Please enter a new password'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }

    setLoading(true);
    try {
      await authApi.resetPassword(userId, token, password);
      setDone(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'This reset link is invalid or has expired.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <Toaster position="top-center" toastOptions={{
        style: { background: 'var(--popover)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', fontSize: '14px', backdropFilter: 'blur(12px)' },
        success: { iconTheme: { primary: 'var(--primary)', secondary: 'var(--primary-foreground)' } },
        error: { iconTheme: { primary: 'var(--destructive)', secondary: '#fff' } },
      }} />
      <div className={styles.bg}>
        <Image src="/bg.png" alt="" fill priority className={styles.bgImg} />
        <div className={styles.bgOverlay} />
        <div className={styles.blob1} />
        <div className={styles.blob2} />
        <div className={styles.blob3} />
      </div>

      <motion.div
        className={styles.card}
        initial={{ opacity: 0, scale: 0.95, y: 30 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      >
        <Link to="/" className={styles.logo} aria-label="Go to home">
          <img
            src="/cast-logo-full.png"
            alt="BoldscholarCast"
            className={styles.logoImg}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </Link>

        {!linkIsValid ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <FiXCircle size={44} color="var(--destructive)" />
            </div>
            <h1 className={styles.heading}>Invalid Link</h1>
            <p className={styles.sub}>This password reset link is missing required information.</p>
            <Link to="/forgot-password" className={styles.submitBtn} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
              Request a New Link
            </Link>
          </>
        ) : done ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <FiCheckCircle size={44} color="var(--primary)" />
            </div>
            <h1 className={styles.heading}>Password Reset</h1>
            <p className={styles.sub}>Your password has been updated. You can now sign in.</p>
            <button
              type="button"
              className={styles.submitBtn}
              onClick={() => navigate('/login')}
            >
              Go to Sign In
            </button>
          </>
        ) : (
          <>
            <h1 className={styles.heading}>Reset Password</h1>
            <p className={styles.sub}>Choose a new password for your account.</p>

            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="password">New Password</label>
                <div className={styles.inputWrap}>
                  <FiLock className={styles.inputIcon} />
                  <input
                    id="password"
                    type={showPwd ? 'text' : 'password'}
                    className={styles.input}
                    placeholder="Min. 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    className={styles.eyeBtn}
                    onClick={() => setShowPwd((v) => !v)}
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                  >
                    {showPwd ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="confirmPassword">Confirm Password</label>
                <div className={styles.inputWrap}>
                  <FiLock className={styles.inputIcon} />
                  <input
                    id="confirmPassword"
                    type={showPwd ? 'text' : 'password'}
                    className={styles.input}
                    placeholder="Re-enter your new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>
              </div>

              <motion.button
                type="submit"
                className={styles.submitBtn}
                disabled={loading}
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
              >
                {loading ? <span className={styles.spinner} /> : 'Reset Password'}
              </motion.button>
            </form>
          </>
        )}

        <div className={styles.divider}><span>or</span></div>
        <Link to="/login" className={styles.browseLink}>Back to Sign In</Link>
      </motion.div>
    </div>
  );
}
